// @ts-nocheck
/**
 * In-page tab router. A tap on Swipe, Explore, Likes or Profil fetches that page and swaps it into this
 * document instead of loading a new one; the Plan tab stays a full page load (its state machine is the
 * riskiest part), and so does every other link. Each page visited this way stays alive: its <main> content
 * and its top bar are taken out of the document and kept in memory, so coming back shows it as it was
 * (deck position, scroll, filters), and its scripts run only once.
 *
 *  - Out of the document, a view's elements never answer a document.querySelector(), so the pages' scripts
 *    keep working with their own ids (#recipeGrid, #app-settings ...) and global lookups.
 *  - New content: stylesheets are loaded before it is shown, scripts are re-created so they run (in order,
 *    after the content is in the document; a src that already ran is skipped).
 *  - History: one entry per tab switch, tagged { vhpRoute: key }; popstate swaps back. Entries of the
 *    sheets / reel / cook mode / search carry the tag along and are left to them.
 *  - Analytics: GA page_view per switch (as gtag('config') does on a page load) and the Shopify customer
 *    event vhp_page_viewed (page_viewed itself is Shopify's own, sent on real page loads only).
 *  - Events: vhp:view-hide (before a view leaves) and vhp:view-show (after it is in), detail { key, tab, url, first }.
 *  - Anything unexpected (fetch fails, a redirect, no app shell in the response, a layer open) ends in a
 *    normal page load of the same URL.
 */
(() => {
  'use strict';

  if (window.vhpRouter) return;

  const TAB_ORDER = ['swipe', 'explore', 'plan', 'likes', 'profile'];
  // Tabs rendered with data-app-route (snippets/app-tabbar.liquid); the speculation rules skip those.
  const ROUTED = new Set(['swipe', 'explore', 'likes', 'profile']);
  const DIR_KEY = 'app-shell-dir'; // set by assets/app-shell.js for cross-document transitions
  // Page-dependent body classes (layout/theme.liquid).
  const BODY_CLASSES = ['app-feed', 'app-feed--recipe', 'app-feed--collection', 'vhp-planner-page'];
  // History entries of layers on top of a page (recipe sheet, reel, cook mode, search).
  const LAYER_KEYS = ['vhpLayer', 'vhpReel', 'vhpCook', 'vhpSearch'];
  const META = [
    'meta[name="description"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:url"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'link[rel="canonical"]',
  ];
  const CSS_WAIT_MS = 1500;

  const shell = document.querySelector('.app-shell');
  const phone = shell?.querySelector('.app-phone');
  const main = document.getElementById('MainContent');
  const tabbar = document.querySelector('[data-app-tabbar]');
  if (!shell || !phone || !main || !tabbar || document.body.classList.contains('vhp-planner-page')) return;
  if (!window.fetch || !window.DOMParser || !history.pushState) return;

  const views = new Map();
  const pending = new Map(); // url -> Promise<string> (page HTML)
  let active = null;
  let busy = false;
  let resync = false; // back / forward arrived during a switch
  let queued = null; // a tab tapped during a switch: it goes next (the last tap wins)

  /* ---------------------------------------------------------------- routes */

  const tabLinks = () => Array.from(tabbar.querySelectorAll('.app-tab[data-tab]'));
  const routeOf = (tab) => {
    if (!ROUTED.has(tab)) return null;
    const link = tabLinks().find((el) => el.dataset.tab === tab);
    return link ? new URL(link.href, location.href) : null;
  };
  const samePath = (a, b) => a.replace(/\/+$/, '') === b.replace(/\/+$/, '');
  // The view a location belongs to: a routed tab's page, or the page this document started on.
  const keyFor = (url) => {
    for (const tab of ROUTED) {
      const route = routeOf(tab);
      if (route && samePath(route.pathname, url.pathname)) return tab;
    }
    const first = views.get(initialKey);
    return first && samePath(new URL(first.url, location.href).pathname, url.pathname) ? initialKey : null;
  };

  /* ---------------------------------------------------------------- views */

  // Nodes of the top bar: everything in the phone column before <main> (header + its settings / filter sheet).
  const topNodes = () => {
    const out = [];
    for (let node = phone.firstChild; node && node !== main; node = node.nextSibling) out.push(node);
    return out;
  };

  const metaOf = (doc) =>
    META.map((sel) => {
      const el = doc.querySelector(sel);
      return el ? el.getAttribute(el.tagName === 'LINK' ? 'href' : 'content') : null;
    });

  const capture = (key) => ({
    key,
    tab: shell.dataset.appTab,
    url: location.href,
    title: document.title,
    meta: metaOf(document),
    template: main.dataset.template || '',
    body: BODY_CLASSES.filter((cls) => document.body.classList.contains(cls)),
    top: topNodes(),
    nodes: Array.from(main.childNodes),
    scroll: 0,
    playing: [],
    shown: true,
  });

  const initialUrl = new URL(location.href);
  let initialKey = shell.dataset.appTab;
  if (!ROUTED.has(initialKey) || !samePath(routeOf(initialKey).pathname, initialUrl.pathname)) {
    initialKey = `page:${initialUrl.pathname}`;
  }
  active = capture(initialKey);
  views.set(initialKey, active);

  const tag = (key) => ({ ...(history.state || {}), vhpRoute: key });
  const isLayer = (state) => LAYER_KEYS.some((key) => state?.[key]);

  // Pages change their own URL (the feed's ?feed=, the Explore search ...). Their entries keep the view's tag,
  // and the view remembers its URL for the next time it is shown. Layers (sheet, reel, cook, search) only add
  // entries on top of a page: they keep the tag, the page's URL stays.
  ['pushState', 'replaceState'].forEach((method) => {
    const original = history[method].bind(history);
    history[method] = (state, unused, url) => {
      const own = state && typeof state === 'object' && !Array.isArray(state);
      const tagged = own && state.vhpRoute ? state : { ...(own ? state : {}), vhpRoute: active.key };
      original(tagged, unused, url);
      if (!isLayer(tagged) && tagged.vhpRoute === active.key) active.url = location.href;
    };
  });

  try {
    history.replaceState(tag(initialKey), '');
    // Each view restores its own scroll; the browser's restore would fight it on back / forward.
    history.scrollRestoration = 'manual';
  } catch (_) {}

  const emit = (type, view, first) =>
    window.dispatchEvent(new CustomEvent(type, { detail: { key: view.key, tab: view.tab, url: view.url, first: Boolean(first) } }));

  // Out of the document (its media pauses by itself). Open popovers are closed first. What the view is gets
  // read now, not when it was built: pages move their own nodes around (Explore puts its picks row straight
  // under <main>), and whatever is there belongs to the view that is leaving.
  const detach = (view) => {
    emit('vhp:view-hide', view, false);
    view.top = topNodes();
    view.nodes = Array.from(main.childNodes);
    view.scroll = window.scrollY;
    view.anims = saveAnims(view);
    view.playing = [];
    const all = view.top.concat(view.nodes);
    all.forEach((node) => {
      if (!(node instanceof Element)) return;
      node.querySelectorAll('video').forEach((video) => {
        if (!video.paused) view.playing.push(video);
      });
      [node, ...node.querySelectorAll('[popover]')].forEach((el) => {
        try {
          if (el.matches?.(':popover-open')) el.hidePopover();
        } catch (_) {}
      });
    });
    const parked = document.createDocumentFragment();
    all.forEach((node) => parked.append(node));
  };

  // CSS animations start over when their element is put back into the document: the swipe hint nudged again
  // on every return to the feed (and a transition starting during the nudge was slow to capture). Running ones
  // carry on where they were; finite ones that had played out (not running when the view left) stay finished.
  const animKey = (anim) => `${anim.effect.pseudoElement || ''}|${anim.animationName}`;
  const viewAnims = (view) =>
    view.top.concat(view.nodes).flatMap((node) =>
      node instanceof Element ? node.getAnimations({ subtree: true }).filter((anim) => anim instanceof CSSAnimation && anim.effect?.target) : []
    );
  const saveAnims = (view) => {
    const saved = new Map();
    viewAnims(view).forEach((anim) => {
      const target = anim.effect.target;
      if (!saved.has(target)) saved.set(target, new Map());
      saved.get(target).set(animKey(anim), anim.currentTime);
    });
    return saved;
  };
  // The browser creates them anew only when it next styles the view (inside the swap or up to a frame or two
  // later), so this runs now and on the next frames; each one is fixed once.
  const restoreAnims = (view) => {
    const saved = view.anims;
    view.anims = null;
    if (!saved) return;
    const done = new WeakSet();
    const fix = () =>
      viewAnims(view).forEach((anim) => {
        if (done.has(anim)) return;
        done.add(anim);
        const time = saved.get(anim.effect.target)?.get(animKey(anim));
        try {
          if (time != null) anim.currentTime = time;
          else if (Number.isFinite(anim.effect.getComputedTiming().endTime)) anim.finish();
        } catch (_) {}
      });
    fix();
    requestAnimationFrame(() => {
      fix();
      requestAnimationFrame(fix);
    });
  };


  // Page state first, content last: custom elements in the content read layout as they connect, and a style
  // pass then must already see the right body classes and tab (otherwise everything transitions from the
  // previous tab's look).
  const attach = (view) => {
    main.dataset.template = view.template;
    shell.dataset.appTab = view.tab;
    BODY_CLASSES.forEach((cls) => document.body.classList.toggle(cls, view.body.includes(cls)));
    document.title = view.title;
    META.forEach((sel, i) => {
      const el = document.querySelector(sel);
      if (el && view.meta[i] != null) el.setAttribute(el.tagName === 'LINK' ? 'href' : 'content', view.meta[i]);
    });
    tabLinks().forEach((link) => {
      const on = link.dataset.tab === view.tab;
      link.classList.toggle('is-active', on);
      if (on) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    // <main> before the top bar: the bar's styles depend on the page under it (the feed's :has() rules).
    main.append(fragmentOf(view.nodes));
    phone.insertBefore(fragmentOf(view.top), main);
  };

  const fragmentOf = (nodes) => {
    const frag = document.createDocumentFragment();
    nodes.forEach((node) => frag.append(node));
    return frag;
  };

  /* ---------------------------------------------------------------- loading a page */

  const fetchPage = (url) => {
    const href = url.href;
    if (!pending.has(href)) {
      const request = fetch(href, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then((response) => {
        const final = new URL(response.url || href);
        if (!response.ok || !samePath(final.pathname, url.pathname)) throw new Error(`route ${response.status} ${final.pathname}`);
        return response.text();
      });
      pending.set(href, request);
      request.catch(() => pending.delete(href));
    }
    return pending.get(href);
  };

  const RUNNABLE = /^(|text\/javascript|application\/javascript|module)$/i;

  // A view from a fetched page. Its scripts are inert copies until runScripts().
  const build = (key, url, html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const docShell = doc.querySelector('.app-shell');
    const docPhone = docShell?.querySelector('.app-phone');
    const docMain = doc.getElementById('MainContent');
    if (!docShell || !docPhone || !docMain || doc.body.classList.contains('vhp-planner-page')) throw new Error('route: no app shell');
    if (docShell.dataset.appTab !== key) throw new Error('route: other tab');
    const top = [];
    for (let node = docPhone.firstChild; node && node !== docMain; node = node.nextSibling) top.push(document.importNode(node, true));
    const pageCss = Array.from(doc.body.querySelectorAll('link[rel="stylesheet"]'))
      .filter((link) => !docPhone.contains(link))
      .map((link) => new URL(link.getAttribute('href'), url).href);
    return {
      key,
      tab: docShell.dataset.appTab,
      url: url.href,
      title: doc.title,
      meta: metaOf(doc),
      template: docMain.dataset.template || '',
      body: BODY_CLASSES.filter((cls) => doc.body.classList.contains(cls)),
      top,
      pageCss,
      nodes: Array.from(docMain.childNodes, (node) => document.importNode(node, true)),
      scroll: 0,
      playing: [],
      shown: false,
    };
  };

  const elementsOf = (view, sel) =>
    view.top.concat(view.nodes).flatMap((node) => (node instanceof Element ? [...(node.matches(sel) ? [node] : []), ...node.querySelectorAll(sel)] : []));

  const capped = (promise, ms) => Promise.race([promise, new Promise((resolve) => window.setTimeout(resolve, ms))]);
  const loaded = (el) =>
    new Promise((resolve) => {
      if (el.rel === 'stylesheet' && el.sheet) return resolve();
      el.addEventListener('load', resolve, { once: true });
      el.addEventListener('error', resolve, { once: true });
    });

  /* ---------------------------------------------------------------- stylesheets */

  // A view's own stylesheets live in <head>, one <link> per URL, added when the view is first shown and then
  // switched with the CSSStyleSheet's own `disabled` (synchronous, the parsed sheet stays). link.media and
  // link.disabled both make Chromium drop the sheet and load it again, so a returning page was styled without
  // it for a moment and every transition on it fired. Taken out with their view, they were loaded and parsed
  // again on every return. The layout's own stylesheets are left alone.
  const sheets = new Map(); // href -> { link (once shown), media, on }
  const setSheet = (entry, on) => {
    entry.on = on;
    if (entry.link.sheet) entry.link.sheet.disabled = !on;
    else loaded(entry.link).then(() => entry.link.sheet && (entry.link.sheet.disabled = !entry.on));
  };
  const layoutHrefs = new Set(Array.from(document.head.querySelectorAll('link[rel="stylesheet"]'), (link) => link.href));
  const makeSheet = (href, media) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    if (media && media !== 'all') link.media = media;
    document.head.append(link);
    return link;
  };

  // Takes a view's <link rel=stylesheet> out of its nodes and notes them as the view's sheets.
  const takeSheets = (view) => {
    view.sheets = new Set();
    elementsOf(view, 'link[rel="stylesheet"]').forEach((el) => {
      const href = el.href;
      if (!href || layoutHrefs.has(href)) return;
      view.sheets.add(href);
      if (!sheets.has(href)) sheets.set(href, { link: null, media: el.getAttribute('media') || 'all' });
      const entry = sheets.get(href);
      if (el.isConnected) {
        // The page this document started with: a copy in <head> now, its own goes once the copy is in.
        if (!entry.link) {
          entry.link = makeSheet(href, entry.media);
          entry.on = true;
        }
        loaded(entry.link).then(() => el.remove());
      } else {
        el.remove();
        view.nodes = view.nodes.filter((node) => node !== el);
        view.top = view.top.filter((node) => node !== el);
      }
    });
  };

  // A first visit's stylesheets into the cache before the swap (so adding them there is quick). Page-level ones
  // outside the app shell (the search's on Explore) are global: in they go, as on a page load.
  const warmSheets = (view) => {
    const loads = [];
    view.sheets.forEach((href) => {
      if (sheets.get(href).link) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'style';
      link.href = href;
      loads.push(loaded(link).then(() => link.remove()));
      document.head.append(link);
    });
    (view.pageCss || []).forEach((href) => {
      if (layoutHrefs.has(href) || document.querySelector(`link[rel="stylesheet"][href="${CSS.escape(href)}"]`)) return;
      layoutHrefs.add(href);
      loads.push(loaded(makeSheet(href)));
    });
    return capped(Promise.all(loads), CSS_WAIT_MS);
  };

  // On for the view on screen, off for the others; resolves once a first visit's sheets are in.
  const applySheets = (view) => {
    const loads = [];
    sheets.forEach((entry, href) => {
      const on = Boolean(view.sheets?.has(href));
      if (on && !entry.link) {
        entry.link = makeSheet(href, entry.media);
        entry.on = true;
        loads.push(loaded(entry.link));
      } else if (entry.link && entry.on !== on) {
        setSheet(entry, on);
      }
    });
    return loads.length ? capped(Promise.all(loads), CSS_WAIT_MS) : null;
  };

  // A new view's scripts, fetched before it is shown so they run straight from the cache during the swap.
  const preloadScripts = (view) => {
    const loads = elementsOf(view, 'script[src]')
      .filter((script) => RUNNABLE.test(script.type || '') && !ranSrc.has(script.src))
      .map((script) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'script';
        link.href = script.src;
        const done = new Promise((resolve) => {
          link.onload = link.onerror = resolve;
        }).then(() => link.remove());
        document.head.append(link);
        return done;
      });
    return capped(Promise.all(loads), CSS_WAIT_MS);
  };

  // Script URLs that already ran in this document (page scripts, earlier views, loaded on demand).
  const ranSrc = new Set();
  const noteScripts = () => Array.from(document.scripts, (script) => script.src && ranSrc.add(script.src));
  noteScripts();

  // Like the parser would: in document order, each src script loaded before the next one runs.
  const runScripts = async (view) => {
    for (const old of elementsOf(view, 'script')) {
      if (!RUNNABLE.test(old.type || '')) continue;
      if (old.src && ranSrc.has(old.src)) continue;
      const script = document.createElement('script');
      Array.from(old.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
      if (old.src) {
        ranSrc.add(old.src);
        script.async = false;
        const done = new Promise((resolve) => {
          script.onload = script.onerror = resolve;
        });
        old.replaceWith(script);
        await done;
      } else {
        script.textContent = old.textContent;
        old.replaceWith(script);
      }
    }
  };

  /* ---------------------------------------------------------------- switching */

  const motionOk = () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const setDir = (from, to) => {
    const a = TAB_ORDER.indexOf(from);
    const b = TAB_ORDER.indexOf(to);
    if (a === -1 || b === -1 || a === b) delete document.documentElement.dataset.appDir;
    else document.documentElement.dataset.appDir = b > a ? 'forward' : 'back';
  };

  // The swap itself, inside a same-document view transition where there is one (same CSS as the
  // cross-document tab transition in assets/app-shell.css). Everything the new view needs happens in here,
  // before its snapshot is taken, so nothing pops in or competes with the animation afterwards: its
  // stylesheets switch on, a first visit's scripts run (preloaded, so from the cache; cap 1 s), videos resume,
  // vhp:view-show listeners repaint.
  const swap = (view, first) => {
    const from = active;
    const run = async () => {
      detach(from);
      // Its stylesheets first (a first visit's are added and loaded from the cache here): content inserted before
      // them is styled once without them, and everything on it transitions.
      const adding = applySheets(view);
      if (adding) await adding;
      attach(view);
      restoreAnims(view);
      active = view;
      const restore = () => window.scrollTo({ top: view.scroll, behavior: 'instant' });
      restore();
      if (first) {
        views.set(view.key, view);
        view.shown = true;
        await capped(runScripts(view), 1000);
      }
      arrive(view);
      emit('vhp:view-show', view, first);
      restore();
    };
    // The loader off before the old view is captured: the transition starts from the page, not a dimmed one.
    window.vhpAppShell?.busy(false, true);
    setDir(from.tab, view.tab);
    let transition = null;
    if (document.startViewTransition && motionOk()) {
      try {
        transition =
          'types' in (window.ViewTransition?.prototype || {})
            ? document.startViewTransition({ update: run, types: ['page-navigation', 'app-route'] })
            : document.startViewTransition(run);
      } catch (_) {
        transition = null;
      }
    }
    if (!transition) {
      delete document.documentElement.dataset.appDir;
      return run();
    }
    transition.finished.finally(() => delete document.documentElement.dataset.appDir);
    // Tap through: the next interaction should not wait for the animation. While it runs, the tab bar is only
    // painted from its snapshot and a tap lands on <html>: find the tab under the finger and pass the tap on.
    document.addEventListener(
      'pointerdown',
      (event) => {
        transition.skipTransition();
        if (event.target !== document.documentElement) return;
        const hit = tabLinks().find((link) => {
          const r = link.getBoundingClientRect();
          return event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
        });
        if (!hit) return;
        const onClick = (click) => {
          if (click.target === document.documentElement) hit.click();
        };
        document.addEventListener('click', onClick, { once: true, capture: true });
        window.setTimeout(() => document.removeEventListener('click', onClick, true), 1000);
      },
      { once: true, capture: true }
    );
    return transition.updateCallbackDone;
  };

  const arrive = (view) => {
    const tab = tabbar.querySelector('.app-tab.is-active');
    tab?.classList.add('is-arriving');
    tab?.addEventListener('animationend', () => tab.classList.remove('is-arriving'), { once: true });
    view.playing.forEach((video) => {
      if (video.isConnected && video.dataset.userPaused !== '1') video.play()?.catch?.(() => {});
    });
    view.playing = [];
  };

  let lastUrl = location.href;
  const pageView = (view) => {
    const referrer = lastUrl;
    lastUrl = location.href;
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', { page_location: location.href, page_title: document.title, page_referrer: referrer });
      }
    } catch (_) {}
    try {
      window.Shopify?.analytics?.publish?.('vhp_page_viewed', { url: location.href, title: document.title, tab: view.tab, referrer });
    } catch (_) {}
  };

  const hardLoad = (url) => {
    window.location.assign(url);
  };

  // push: a tab tap (new history entry); otherwise popstate (the entry is already there).
  const go = async (key, url, push) => {
    if (busy) {
      if (push) queued = { key, url };
      return;
    }
    if (key === active.key) return;
    busy = true;
    try {
      let view = views.get(key);
      const first = !view;
      // Not in memory yet: the shell dims the page and shows its loader if this takes a moment.
      if (!view) window.vhpAppShell?.busy(true);
      if (!view) {
        view = build(key, url, await fetchPage(url));
        pending.delete(url.href);
        takeSheets(view);
        await Promise.all([warmSheets(view), preloadScripts(view)]);
      }
      if (push) history.pushState(tag(key), '', view.url);
      // Before the new view's inert copies are in the document.
      if (first) noteScripts();
      await swap(view, first);
      pageView(view);
    } catch (err) {
      busy = false;
      hardLoad(push ? url.href : location.href);
      return;
    }
    busy = false;
    if (resync) {
      resync = false;
      sync();
    } else if (queued) {
      const next = queued;
      queued = null;
      go(next.key, next.url, true);
    }
  };

  // The history entry decides which view is on screen.
  const sync = () => {
    const key = history.state?.vhpRoute || keyFor(new URL(location.href));
    if (!key || key === active.key) return;
    if (views.has(key)) go(key, new URL(location.href), false);
    else hardLoad(location.href);
  };

  // The page this document started with: its stylesheets move to <head> too (a copy first, then the original
  // goes), so leaving and coming back never reloads them.
  takeSheets(views.get(initialKey));

  /* ---------------------------------------------------------------- wiring */

  const layerOpen = () => LAYER_KEYS.some((key) => history.state?.[key]) || document.documentElement.classList.contains('vhp-s-open');

  // Capture: runs before the tab's other click handlers (the Explore tab's "second tap opens search" would
  // otherwise see the new page already in place).
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('.app-tab[data-app-route]');
      if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      try {
        sessionStorage.removeItem(DIR_KEY);
      } catch (_) {}
      const key = link.dataset.tab;
      const url = routeOf(key);
      if (!url || layerOpen()) return;
      if (key === active.key && !busy) {
        // Second tap on the tab you are on: back to the top (Explore: assets/vhp-search.js handles it).
        if (key === 'explore') return;
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: motionOk() ? 'smooth' : 'auto' });
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      window.vhpTrack?.('tab_switch', { from: active.tab, to: key, cached: views.has(key) });
      go(key, url, true);
    },
    { capture: true }
  );

  // Links to a tab outside the tab bar (the top bar's "Explore"): same as tapping that tab.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('[data-app-tab-link]');
      const tab = link && tabbar.querySelector(`.app-tab[data-app-route][data-tab="${CSS.escape(link.dataset.appTabLink)}"]`);
      if (!tab || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      tab.click();
    },
    { capture: true }
  );

  // A finger on a tab: start fetching it (the click comes ~100 ms later).
  const warm = (event) => {
    const link = event.target.closest?.('.app-tab[data-app-route]');
    const key = link?.dataset.tab;
    if (key && !views.has(key)) fetchPage(routeOf(key)).catch(() => {});
  };
  tabbar.addEventListener('pointerdown', warm, { passive: true });
  tabbar.addEventListener('mouseover', warm, { passive: true });

  window.addEventListener('popstate', () => {
    if (busy) resync = true;
    else sync();
  });

  // After the page has settled: fetch the other tabs' HTML in the background (not on Save-Data / 2G),
  // so the first switch to each needs no network. Nothing runs until it is shown.
  const warmAll = () => {
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    ROUTED.forEach((tab) => {
      if (!views.has(tab)) fetchPage(routeOf(tab)).catch(() => {});
    });
  };
  const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(warmAll, { timeout: 4000 }) : warmAll());
  if (document.readyState === 'complete') window.setTimeout(idle, 3000);
  else window.addEventListener('load', () => window.setTimeout(idle, 3000), { once: true });

  window.vhpRouter = {
    get active() {
      return active.key;
    },
    go: (tab) => {
      const url = routeOf(tab);
      if (url) go(tab, url, true);
      else if (tab) hardLoad(tabLinks().find((el) => el.dataset.tab === tab)?.href || '/');
    },
  };
})();
